import { useState } from 'react';
import { Badge, Menu, Text, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconLock,
  IconUser,
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { updateProfileVisibility } from '../../services/userIdentityService';
import { patchCachedUserProfile } from '../../hooks/useAppUser';

type ProfileVisibilityBadgeProps = {
  profilePublic: boolean;
  onChange: (profilePublic: boolean) => void;
  /** Responsive twins: the header renders one for each breakpoint. */
  visibleFrom?: string;
  hiddenFrom?: string;
  style?: React.CSSProperties;
};

/**
 * The Public/Private badge on someone's own cookbook, as the control rather
 * than a readout.
 *
 * Reachable state: it says what it is, and picking the other option from the
 * menu is the whole interaction. A menu rather than a switch on purpose — one
 * stray tap should not publish a cookbook, and the two options can carry the
 * sentence that says what each means. Preferences → Account keeps the fuller
 * explanation and writes the same field.
 */
export const ProfileVisibilityBadge = ({
  profilePublic,
  onChange,
  visibleFrom,
  hiddenFrom,
  style,
}: ProfileVisibilityBadgeProps) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const apply = async (next: boolean) => {
    if (!user || saving || next === profilePublic) return;
    setSaving(true);
    // Optimistic: the badge is the thing being clicked, so it should change
    // under the cursor. Put it back if the write fails.
    onChange(next);
    try {
      const token = await user.getIdToken();
      await updateProfileVisibility(next, token);
      patchCachedUserProfile(user.uid, { profilePublic: next });
      notifications.show({
        title: next ? 'Cookbook is public' : 'Cookbook is private',
        message: next
          ? 'Anyone with the link can see it now.'
          : 'Only you can see it now.',
        color: next ? 'green' : 'blue',
        icon: <IconCheck size={18} />,
      });
    } catch {
      onChange(!next);
      notifications.show({
        title: "Couldn't change visibility",
        message: 'Something went wrong. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setSaving(false);
    }
  };

  const option = (value: boolean, label: string, hint: string) => (
    <Menu.Item
      leftSection={value ? <IconUser size={16} /> : <IconLock size={16} />}
      rightSection={
        value === profilePublic ? <IconCheck size={14} /> : undefined
      }
      onClick={() => apply(value)}
    >
      <Stack gap={0}>
        <Text size="sm">{label}</Text>
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      </Stack>
    </Menu.Item>
  );

  return (
    <Menu shadow="md" width={260} position="bottom-start" withinPortal>
      <Menu.Target>
        <Badge
          size="sm"
          variant="light"
          component="button"
          type="button"
          color={profilePublic ? 'green' : 'gray'}
          leftSection={profilePublic ? <IconUser size={12} /> : <IconLock size={12} />}
          rightSection={<IconChevronDown size={12} />}
          visibleFrom={visibleFrom}
          hiddenFrom={hiddenFrom}
          style={{ cursor: 'pointer', ...style }}
        >
          {profilePublic ? 'Public' : 'Private'}
        </Badge>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Who can see this cookbook</Menu.Label>
        {option(true, 'Public', 'Anyone with the link')}
        {option(false, 'Private', 'Only you')}
      </Menu.Dropdown>
    </Menu>
  );
};
